<template>
    <el-popover
        v-model:visible="vis"
        trigger="click"
        :popper-style="{ width: `${width}px`, padding: 0 }"
        :hide-after="0"
    >

        <!-- input -->
        <template #reference>
            <el-input
                ref="input"
                v-model="val"
                v-bind="$attrs"
                @input="refresh()"
                @keyup.enter="onSearch()"
            >
                <template #suffix>
                    <CircleClose
                        v-if="val"
                        p4
                        w16
                        h16
                        cursor-pointer
                        @click.stop="onClear"
                    />
                    <Search
                        p4
                        ml4
                        w16
                        h16
                        cursor-pointer
                        @click.stop="onSearch"
                    />
                </template>
            </el-input>
        </template>

        <!-- header -->
        <div
            flex="~ wrap"
            p12
            mb--12
            text-14
            @click="input.focus()"
        >
            <div
                v-for="opt in keysed"
                :key="opt.value"
                :class="['btn', keys.includes(opt.value) && 'is-active']"
                mr8
                mb8
                lh-22
                text-black
                @click="toggle(opt)"
            >{{ opt.label }}</div>
        </div>

        <div b-b="1 solid #00000015" />

        <!-- options -->
        <div
            v-if="options?.length && !loading"
            max-h500
            overflow-auto
            py8
        >
            <div
                v-for="(opt,index) in options"
                :key="index"
                class="item"
                truncate
                @click="onSelect(opt)"
                v-html="highlight(typeof label == 'function' ? label(opt, keys) : opt[label])"
            />
        </div>

        <div
            v-if="!options?.length && !loading"
            py14
            text="#00000080"
            text-center
        >
            无数据
        </div>

        <!-- 加载中 -->
        <div
            v-if="loading"
            py11
            text="#00000080"
            text-center
            lh="1em"
        >
            <el-icon
                class="is-loading"
                text-22
            ><Loading /></el-icon>
        </div>
    </el-popover>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';
import { useElementSize, useVModel } from '@vueuse/core';
import { remove } from '@vue/shared';
import { Search, CircleClose } from '@element-plus/icons-vue';
import { useRequest } from 'vue-request';

const props = defineProps({
    modelValue: String,
    fetchKeys: Function,
    fetchOptions: Function,
    label: [String, Function],
    single: Boolean
});

const emit = defineEmits(['update:modelValue', 'search', 'clear', 'update:keys', 'select']);

const vis = ref(false);

const val = useVModel(props, 'modelValue', undefined, { passive: true });
const keys = ref([]);

watch(val, (val) => val && (vis.value = true));

const { data: keysed } = useRequest(async() => (await props.fetchKeys()).map(nOpt));

const { data: options, refresh, loading } = useRequest(async(kw) => (await props.fetchOptions(val.value, keys.value)).map(nOpt), { manual: true, throttleInterval: 600 });

function toggle(opt) {
    if (keys.value.includes(opt.value)) {
        remove(keys.value, opt.value);
    } else {
        if (props.single) keys.value.length = 0;
        keys.value.push(opt.value);
    }
    refresh();
}

const input = ref();
const { width } = useElementSize(input);

async function onSelect(row) {
    emit('update:keys', keys.value);
    const label = typeof props.label === 'function' ? props.label(row, keys.value) : row[props.label];
    emit('update:modelValue', label);
    emit('select', row);
    setTimeout(() => {
        vis.value = false;
    }, 0);
}

async function onClear() {
    emit('update:modelValue', '');
    await nextTick();
    emit('clear');
}

async function onSearch() {
    emit('update:keys', keys.value);
    await nextTick();
    emit('search');
    vis.value = false;
}

function highlight(txt) {
    const kw = props.modelValue;
    if (!kw) return txt;
    if (!txt) return txt;
    return txt.replace(new RegExp(kw, 'ig'), `<span style="color: var(--el-color-primary)">${kw}</span>`);
}

function nOpt(opt) {
    return (
        Array.isArray(opt) ? { label: opt[0], value: opt[1] }
            : typeof opt === 'object' ? opt
                : { label: opt, value: opt }
    );
}
</script>

<style scoped lang="scss">
.btn {
  @apply px-12 rd-full b-(1 solid #00000015) hover:bg-#00000010 cursor-pointer;

  &.is-active {
    @apply  c-#179aee b-#179aee;
  }
}

.item {
  color: var(--el-text-color-regular);
  height: 34px;
  line-height: 34px;
  padding: 0 32px 0 20px;
  cursor: pointer;

  &:hover {
    background: var(--el-fill-color-light);
  }

  &.is-active {
    color: var(--el-color-primary);
  }
}
</style>
